import { useCallback } from 'react';
import { css } from '@emotion/react';
import { paymentsSentry } from '@frontend/payments-hooks';
import { theme } from '@frontend/theme';
import { RadioCardField, useForm, useFormField, useTooltip } from '@frontend/design-system';
import { useCareCreditSmsNumber } from '../../../hooks/use-care-credit-person-sms-number';
import { useCareCreditTranslations } from '../../../hooks/use-care-credit-translations';
import { EnrollmentBaseStep } from '../atoms/step/enrollment-base-step';
import { useCareCreditEnrollmentContext } from '../context/care-credit-enrollment.context';
import { CareCreditEnrollmentMultiSteps, EnrollmentFormProcessMethod } from '../enrollment-multi-step.types';

const styles = {
  radioCardField: css`
    & > div {
      flex-direction: column;
    }
  `,
  radioCardFieldDisabled: css`
    margin-top: ${theme.spacing(2)};

    & > div {
      flex-direction: column;
    }
  `,
};

const CURRENT_STEP: CareCreditEnrollmentMultiSteps = 'select-process-method';
const FORM_STEP: CareCreditEnrollmentMultiSteps = 'new-application-form';
const PAYMENT_METHOD_FORM_VALUE: EnrollmentFormProcessMethod = 'form';
const PAYMENT_METHOD_TTP_VALUE: EnrollmentFormProcessMethod = 'ttp';

export const Step1SelectProcessMethod = () => {
  const { t } = useCareCreditTranslations();

  const {
    person,
    locationId,
    invoice,
    onCancel,
    onGoBack,
    onGoToMessageStep,
    multiStep: { goToStep },
  } = useCareCreditEnrollmentContext();

  const { triggerProps, tooltipProps, Tooltip } = useTooltip({
    placement: 'top-start',
  });

  const { firstValidPhoneNumber } = useCareCreditSmsNumber({
    person,
    locationId,
  });

  const { getFieldProps, isComplete, values } = useForm({
    fields: {
      processMethod: { type: 'radio', required: true },
    },
  });

  const disabledField = useFormField({
    type: 'radio',
  });

  const onClickCancel = useCallback(() => {
    if (onCancel) {
      return onCancel?.();
    }
    paymentsSentry.error('Cancel clicked but no onCancel handler found in context', 'Step1SelectProcessMethod', '');
    return console.warn('Cancel clicked but no onCancel handler found in context');
  }, [onCancel]);

  const onClickNext = useCallback(() => {
    if (values.processMethod === PAYMENT_METHOD_FORM_VALUE) {
      goToStep({
        orStep: FORM_STEP,
      });
    } else if (values.processMethod === PAYMENT_METHOD_TTP_VALUE) {
      onGoToMessageStep();
    } else {
      paymentsSentry.error(
        'Invalid process method received',
        'Step1SelectProcessMethod',
        `Received process method: ${values.processMethod}`
      );
      console.warn('Invalid process method received:', values.processMethod);
    }
  }, [values.processMethod, onGoToMessageStep, goToStep]);

  return (
    <EnrollmentBaseStep
      id={CURRENT_STEP}
      label={t('How would you like to process the CareCredit Prequalification?')}
      footer={{
        primary: {
          children: t('Next'),
          disabled: !isComplete,
        },
        onClickPrimary: onClickNext,
        secondary: {
          children: t('Cancel'),
        },
        onClickSecondary: onClickCancel,
        back: {
          children: t('Back'),
          iconName: 'back',
        },
        onClickBack: onGoBack,

        /**
         * This step might be reached from the payments panel, where there is no invoice.
         * In which case, hide the back button, as there's no "payment methods" step to go back to.
         */
        showBackButton: !!invoice,
      }}
    >
      <RadioCardField
        {...getFieldProps('processMethod')}
        name='processMethod'
        className='select-process-method--radio-card-field'
        css={styles.radioCardField}
      >
        <RadioCardField.Option
          value={PAYMENT_METHOD_FORM_VALUE}
          title={t('Submit in-office')}
          description={t(`Complete CareCredit prequalification on patient's behalf`)}
        />
        {!!firstValidPhoneNumber && (
          <RadioCardField.Option
            value={PAYMENT_METHOD_TTP_VALUE}
            title={t('Send prequalification link')}
            description={t(`Send link to patient's mobile number`)}
          />
        )}
      </RadioCardField>
      {!firstValidPhoneNumber && (
        <>
          <div {...triggerProps}>
            <RadioCardField
              {...disabledField}
              name='processMethodDisabled'
              className='select-process-method--radio-card-field'
              css={styles.radioCardFieldDisabled}
              disabled={true}
            >
              <RadioCardField.Option
                value={PAYMENT_METHOD_TTP_VALUE}
                title={t('Send prequalification link')}
                description={t(`Send link to patient's mobile number`)}
              />
              <span></span>
            </RadioCardField>
          </div>
          <Tooltip {...tooltipProps}>{t('No valid phone number')}</Tooltip>
        </>
      )}
    </EnrollmentBaseStep>
  );
};
